import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Monitor, Smartphone, Trash2, FileText, Download, ExternalLink, Receipt } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const DOC_LABELS = {
  NOT_REQUIRED: 'Non richiesta', REQUESTED: 'Fattura richiesta', TO_ISSUE: 'Da emettere',
  ISSUED: 'Fattura emessa', CANCELLED: 'Annullato',
};

const fmtDt = (d) => d ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

function DevicesTab() {
  const [data, setData] = useState(null);
  const load = () => api.get('/account/devices').then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!window.confirm('Revocare questo dispositivo? Verrà disconnesso immediatamente.')) return;
    await api.delete(`/account/devices/${id}`);
    toast.success('Dispositivo revocato');
    load();
  };

  if (!data) return <div className="h-40 rounded-2xl bg-slate-100 animate-pulse" />;
  return (
    <div data-testid="devices-tab">
      <p className="text-sm text-slate-500 mb-5">
        Puoi usare il tuo account su un massimo di <strong>{data.max_devices}</strong> dispositivi contemporaneamente.
        Accedendo da un nuovo dispositivo oltre il limite, il meno recente viene disconnesso automaticamente.
      </p>
      <div className="space-y-3">
        {data.devices.map((d) => (
          <div key={d.device_id} data-testid={`device-${d.device_id}`}
            className="flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-2xl px-5 py-4">
            <div className="flex items-center gap-3 min-w-0">
              {/iPhone|Android|iPad/.test(d.user_agent || '') ? <Smartphone size={18} className="text-slate-400 shrink-0" strokeWidth={1.5} /> : <Monitor size={18} className="text-slate-400 shrink-0" strokeWidth={1.5} />}
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">
                  {d.label} {d.current && <Badge className="ml-2 bg-emerald-50 text-emerald-700 border-emerald-200 border text-xs">Questo dispositivo</Badge>}
                </div>
                <p className="text-xs text-slate-400">Ultimo accesso: {fmtDt(d.last_seen_at)} · IP {d.ip || '—'}</p>
              </div>
            </div>
            {!d.current && (
              <button onClick={() => revoke(d.device_id)} data-testid={`revoke-${d.device_id}`}
                className="p-2 rounded-lg hover:bg-rose-50 text-rose-500 shrink-0" aria-label="Revoca">
                <Trash2 size={16} strokeWidth={1.5} />
              </button>
            )}
          </div>
        ))}
        {data.devices.length === 0 && <p className="text-sm text-slate-500">Nessun dispositivo attivo.</p>}
      </div>
    </div>
  );
}

function OrdersTab({ goFiscal }) {
  const [orders, setOrders] = useState(null);
  const load = () => api.get('/orders').then(({ data }) => setOrders(data.orders)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const openReceipt = async (o, download = false) => {
    const { data } = await api.post(`/orders/${o.order_id}/receipt-token`);
    const q = `?access=${encodeURIComponent(data.access_token)}${download ? '&download=1' : ''}`;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.order_id}/receipt${q}`,
      '_blank', 'noopener,noreferrer');
  };

  const requestInvoice = async (o) => {
    try {
      await api.post(`/orders/${o.order_id}/request-invoice`);
      toast.success('Richiesta fattura inviata');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella richiesta');
      if (e.response?.status === 400) goFiscal();
    }
  };

  if (orders === null) return <div className="h-40 rounded-2xl bg-slate-100 animate-pulse" />;
  return (
    <div data-testid="orders-tab">
      {orders.length === 0 ? (
        <p className="text-sm text-slate-500">Nessun ordine ancora.</p>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.order_id} data-testid={`order-${o.order_id}`}
              className="bg-white border border-slate-200 rounded-2xl px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{o.contest_title || o.contest_id}</p>
                  <p className="text-xs text-slate-400">{fmtDt(o.created_at)} · {o.order_id}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-slate-900">€ {Number(o.amount_gross).toFixed(2)}</span>
                  <Badge className={o.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 border' : 'bg-slate-100 text-slate-500'}>{o.payment_status}</Badge>
                  <Badge className="bg-slate-100 text-slate-600 border-slate-200 border">{DOC_LABELS[o.document_status] || o.document_status}</Badge>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => openReceipt(o)} data-testid={`receipt-view-${o.order_id}`} className="rounded-full">
                  <ExternalLink size={13} strokeWidth={1.5} className="mr-1.5" /> Ricevuta
                </Button>
                <Button variant="outline" size="sm" onClick={() => openReceipt(o, true)} data-testid={`receipt-download-${o.order_id}`} className="rounded-full">
                  <Download size={13} strokeWidth={1.5} className="mr-1.5" /> Scarica
                </Button>
                {o.document_status === 'NOT_REQUIRED' && o.payment_status === 'paid' && (
                  <Button variant="outline" size="sm" onClick={() => requestInvoice(o)} data-testid={`invoice-request-${o.order_id}`} className="rounded-full">
                    <FileText size={13} strokeWidth={1.5} className="mr-1.5" /> Richiedi fattura
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-4">
        La ricevuta è un documento commerciale di conferma, non una fattura fiscale. Per la fattura usa "Richiedi fattura" dopo aver compilato i dati fiscali.
      </p>
    </div>
  );
}

function FiscalTab({ onSaved }) {
  const [fiscal, setFiscal] = useState(null);
  useEffect(() => { api.get('/account/fiscal').then(({ data }) => setFiscal(data.fiscal)).catch(() => {}); }, []);
  if (!fiscal) return <div className="h-40 rounded-2xl bg-slate-100 animate-pulse" />;

  const F = (k, label, testId) => (
    <div>
      <Label>{label}</Label>
      <Input className="mt-1.5" data-testid={testId} value={fiscal[k] || ''} onChange={(e) => setFiscal({ ...fiscal, [k]: e.target.value })} />
    </div>
  );

  const save = async () => {
    await api.put('/account/fiscal', fiscal);
    toast.success('Dati fiscali salvati');
    onSaved?.();
  };

  return (
    <div data-testid="fiscal-tab" className="bg-white border border-slate-200 rounded-2xl p-6">
      <p className="text-sm text-slate-500 mb-5">
        Dati opzionali, necessari solo se intendi richiedere la fattura. Con partita IVA l'ordine viene classificato B2B.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {F('first_name', 'Nome', 'fiscal-first-name')}
        {F('last_name', 'Cognome', 'fiscal-last-name')}
        {F('company_name', 'Ragione sociale (se B2B)', 'fiscal-company')}
        {F('address', 'Indirizzo', 'fiscal-address')}
        {F('country', 'Paese', 'fiscal-country')}
        {F('codice_fiscale', 'Codice fiscale', 'fiscal-cf')}
        {F('partita_iva', 'Partita IVA', 'fiscal-piva')}
        {F('pec', 'PEC', 'fiscal-pec')}
        {F('codice_destinatario', 'Codice destinatario SDI', 'fiscal-sdi')}
      </div>
      <Button onClick={save} data-testid="fiscal-save" className="mt-5 rounded-full bg-brand hover:bg-brand-hover text-white px-7">Salva dati fiscali</Button>
    </div>
  );
}

export default function Account() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'dispositivi';
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="account-page">
      <h1 className="font-display text-2xl sm:text-3xl tracking-tight font-semibold text-slate-900 mb-8" data-testid="account-title">
        <Receipt className="inline-block mr-2 -mt-1" size={24} strokeWidth={1.5} /> Il mio account
      </h1>
      <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v })}>
        <TabsList className="mb-6">
          <TabsTrigger value="dispositivi" data-testid="tab-dispositivi">Dispositivi</TabsTrigger>
          <TabsTrigger value="ordini" data-testid="tab-ordini">Ordini e ricevute</TabsTrigger>
          <TabsTrigger value="fiscali" data-testid="tab-fiscali">Dati fiscali</TabsTrigger>
        </TabsList>
        <TabsContent value="dispositivi"><DevicesTab /></TabsContent>
        <TabsContent value="ordini"><OrdersTab goFiscal={() => setSearchParams({ tab: 'fiscali' })} /></TabsContent>
        <TabsContent value="fiscali"><FiscalTab /></TabsContent>
      </Tabs>
    </div>
  );
}
